import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CardMedia,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import Div from "@jumbo/shared/Div";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import { PROD_URL } from "app/services/config";
import { getCustomer, editDSOKDetails } from "app/services/api/management";
import { getGalleries } from "app/services/api/media";
import SingleVideo from "app/pages/media/videos/selectSingleVideo";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90vh",
  maxWidth: "90vw",
  width: "80vw",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  overflow: "auto",
};

const parseConfig = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value) || {};
    } catch (_) {
      return {};
    }
  }
  return value;
};

/** Z existujúceho galleryUrl (per-jazyk objekt alebo string) vytiahne jednu URL – galériu nerozlišujeme podľa jazyka. */
const resolveSingleGalleryUrl = (galleryUrl) => {
  if (!galleryUrl) return "";
  if (typeof galleryUrl === "string") return galleryUrl;
  if (typeof galleryUrl === "object") {
    const firstNonEmpty = Object.values(galleryUrl).find((v) => v);
    return firstNonEmpty || "";
  }
  return "";
};

/** Zoznam jazykov zákazníka (customer.languages môže byť pole alebo objekt). */
const normalizeLanguages = (langsRaw) => {
  if (Array.isArray(langsRaw)) return langsRaw.filter(Boolean);
  if (langsRaw && typeof langsRaw === "object")
    return Object.keys(langsRaw).filter((k) => langsRaw[k]);
  return [];
};

/**
 * Záložka "Úvodná obrazovka" v Správe kioskov.
 * Povinné nastavenie welcomeScreenType ("gallery" | "video", default "gallery") určuje,
 * čo sa reálne zobrazí na kiosku. Výber galérie aj videa je vždy prístupný v podzáložkách.
 *  - galéria -> JEDNA galéria (rovnaká pre všetky jazyky)
 *  - video   -> JEDNO video (globálne)
 *
 * POZOR: ukladanie zatiaľ NIE JE implementované (na výslovný pokyn) – táto verzia iba
 * načíta a zobrazí aktuálny stav a umožní výber; hodnoty sa držia v lokálnom stave.
 */
const WelcomeScreenTab = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const authUser = getAuthUser() || {};
  const customerId = authUser?.customerId;
  const userId = authUser?.userId;
  const customerUrl = authUser?.customer?.url || authUser?.url || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // welcomeScreenType: "gallery" | "video" (povinné, default "gallery")
  const [mode, setMode] = useState("gallery");
  const [innerTab, setInnerTab] = useState("gallery");
  const [galleryUrl, setGalleryUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [galleries, setGalleries] = useState([]);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cust = await getCustomer(customerUrl);
      const settings = parseConfig(cust?.dsokSettings);
      const mainScreen = settings?.settings?.mainScreen || {};
      const slider = mainScreen?.slider || {};

      const type = mainScreen.welcomeScreenType === "video" ? "video" : "gallery";
      setMode(type);
      setInnerTab(type);
      setGalleryUrl(resolveSingleGalleryUrl(slider.galleryUrl));
      setVideoUrl(
        typeof mainScreen.videoSliderUrl === "string"
          ? mainScreen.videoSliderUrl
          : "",
      );
    } catch (_) {
      /* prázdny stav */
    }
    try {
      const list = await getGalleries(customerId, userId);
      setGalleries(Array.isArray(list) ? list : []);
    } catch (_) {
      setGalleries([]);
    }
    setLoading(false);
  }, [customerUrl, customerId, userId]);

  useEffect(() => {
    if (customerUrl) load();
    else setLoading(false);
  }, [customerUrl, load]);

  const galleryOptions = useMemo(
    () =>
      galleries.map((g) => ({
        url: g.url,
        label: g.label || g.url,
      })),
    [galleries],
  );

  const handleVideoSelect = (video) => {
    if (video?.filename) {
      setVideoUrl(
        `${PROD_URL}/uploads/files/${customerId}/Videos/${video.filename}`,
      );
    }
    setVideoModalOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Načítame ČERSTVÝ celý config a zmeníme iba potrebné polia v mainScreen,
      // aby sme neprepísali ostatné nastavenia (mirror SmartkioskDetails save).
      const cust = await getCustomer(customerUrl);
      const details = parseConfig(cust?.dsokDetails);
      const settings = parseConfig(cust?.dsokSettings);
      const next = { ...details, ...settings };
      next.settings = { ...(next.settings || {}) };
      next.settings.mainScreen = { ...(next.settings.mainScreen || {}) };
      const ms = next.settings.mainScreen;

      // Režim (povinné pole)
      ms.welcomeScreenType = mode;

      // Galéria – tá istá pre VŠETKY jazyky zákazníka; zachovávame štruktúru galleryUrl
      // ako per-jazyk objekt { sk: url, en: url, ... } (getSlider ho tak číta).
      const prevSlider =
        ms.slider && typeof ms.slider === "object" ? ms.slider : {};
      let langs = normalizeLanguages(next.customer?.languages);
      if (langs.length === 0) {
        langs = normalizeLanguages(prevSlider.galleryUrl); // fallback: existujúce jazyky v galleryUrl
      }
      if (langs.length === 0) langs = ["sk"];
      const galleryUrlObj = {};
      langs.forEach((lang) => {
        galleryUrlObj[lang] = galleryUrl || "";
      });
      ms.slider = { ...prevSlider, galleryUrl: galleryUrlObj };

      // Video – globálne, do existujúceho poľa mainScreen.videoSliderUrl
      ms.videoSliderUrl = videoUrl || "";

      await editDSOKDetails({ customerUrl, ...next });
      enqueueSnackbar(t("notifications.wasSuccessful"), { variant: "success" });
    } catch (_) {
      enqueueSnackbar(t("notifications.wasFailed"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" py={5}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <FormControl required>
        <FormLabel sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}>
          {t("pages.kioskManagement.welcomeScreen.modeLabel", {
            defaultValue: "Čo sa má zobrazovať na úvodnej obrazovke",
          })}
        </FormLabel>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel
            value="gallery"
            control={<Radio />}
            label={t("pages.kioskManagement.welcomeScreen.modeGallery", {
              defaultValue: "Galéria",
            })}
          />
          <FormControlLabel
            value="video"
            control={<Radio />}
            label={t("pages.kioskManagement.welcomeScreen.modeVideo", {
              defaultValue: "Video",
            })}
          />
        </RadioGroup>
      </FormControl>

      <TabContext value={innerTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={(_e, val) => setInnerTab(val)}>
            <Tab
              label={t("pages.kioskManagement.welcomeScreen.tabGallery", {
                defaultValue: "Galéria",
              })}
              value="gallery"
            />
            <Tab
              label={t("pages.kioskManagement.welcomeScreen.tabVideo", {
                defaultValue: "Video",
              })}
              value="video"
            />
          </TabList>
        </Box>

        <TabPanel value="gallery" sx={{ px: 0 }}>
          <FormControl fullWidth sx={{ maxWidth: 480 }}>
            <InputLabel>
              {t("pages.kioskManagement.welcomeScreen.gallery", {
                defaultValue: "Galéria pre slider",
              })}
            </InputLabel>
            <Select
              label={t("pages.kioskManagement.welcomeScreen.gallery", {
                defaultValue: "Galéria pre slider",
              })}
              value={galleryUrl || ""}
              onChange={(e) => setGalleryUrl(e.target.value)}
            >
              <MenuItem value="">
                <em>
                  {t("pages.kioskManagement.welcomeScreen.noGallery", {
                    defaultValue: "— žiadna —",
                  })}
                </em>
              </MenuItem>
              {galleryOptions.map((g) => (
                <MenuItem key={g.url} value={g.url}>
                  {g.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TabPanel>

        <TabPanel value="video" sx={{ px: 0 }}>
          {videoUrl ? (
            <CardMedia
              component="video"
              src={videoUrl}
              controls
              sx={{
                width: "100%",
                maxWidth: 640,
                maxHeight: "45vh",
                borderRadius: 1,
                bgcolor: "black",
                mb: 2,
              }}
            />
          ) : (
            <Div
              sx={{
                width: "100%",
                maxWidth: 640,
                height: 200,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                borderRadius: 1,
                border: "1px dashed",
                borderColor: "divider",
                color: "text.secondary",
                mb: 2,
              }}
            >
              <OndemandVideoIcon sx={{ fontSize: 48 }} />
              <Typography variant="body2">
                {t("pages.kioskManagement.welcomeScreen.noVideo", {
                  defaultValue: "Žiadne video nie je vybraté",
                })}
              </Typography>
            </Div>
          )}
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={() => setVideoModalOpen(true)}>
              {t("pages.kioskManagement.welcomeScreen.selectVideo", {
                defaultValue: "Vybrať video",
              })}
            </Button>
            {videoUrl && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setVideoUrl("")}
              >
                {t("pages.kioskManagement.welcomeScreen.removeVideo", {
                  defaultValue: "Odobrať video",
                })}
              </Button>
            )}
          </Stack>
        </TabPanel>
      </TabContext>

      <Box>
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? t("buttons.saving", { defaultValue: "Ukladá sa…" })
            : t("buttons.save", { defaultValue: "Uložiť" })}
        </Button>
      </Box>

      <Modal open={videoModalOpen} onClose={() => setVideoModalOpen(false)}>
        <Div sx={modalStyle}>
          <SingleVideo
            layout={"row-reverse"}
            selectorMode
            onCancel={() => setVideoModalOpen(false)}
            onSelect={handleVideoSelect}
          />
        </Div>
      </Modal>
    </Stack>
  );
};

export default WelcomeScreenTab;
